import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Field, Input, Modal, Notice, Select } from '../design/index.js'
import { useApi } from '../app/api.js'
import { Loading, messageFor } from './feedback.js'

/**
 * Adding a master record without leaving the document that needs it
 * (docs/02 §4c — `Add new address`, `Add new contact`).
 *
 * A site address or a supplier's contact is discovered while raising the order
 * that needs it. The alternative is sending the buyer to the Masters screen and
 * back, and asking them to remember where they were — which is how an order
 * ends up raised against whichever address was already in the box.
 *
 * The form is the master's own field definition, so this stays correct as the
 * master grows fields; only the required ones are asked for, because the point
 * is to get back to the document, not to complete a record.
 */
export interface QuickMasterDialogProps {
  readonly masterKey: string
  readonly title: string
  readonly onClose: () => void
  /** The new record's id, and what it will read as in the lookup. */
  readonly onCreated: (id: string, label: string) => void
}

export const QuickMasterDialog = ({ masterKey, title, onClose, onCreated }: QuickMasterDialogProps) => {
  const api = useApi()
  const client = useQueryClient()
  const def = useQuery({ queryKey: ['masterDef', masterKey], queryFn: () => api.masterDef(masterKey) })
  const [values, setValues] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: () => api.masterCreate(masterKey, values),
    onSuccess: (row) => {
      // Invalidated so the type-ahead behind the dialog can find it at once.
      void client.invalidateQueries({ queryKey: ['lookup', masterKey] })
      const id = String(row['id'] ?? '')
      const label = String(row['name'] ?? row['label'] ?? row['code'] ?? title)
      if (id !== '') onCreated(id, label)
    },
    onError: (err) => setError(messageFor(err)),
  })

  const fields = (def.data?.fields ?? [])
  // Required first, then the rest: somebody is here to get back to a document.
  const asked = [...fields].sort((a, b) => Number(b.required ?? false) - Number(a.required ?? false))
  const incomplete = fields.some((f) => f.required && !(values[f.key] ?? '').trim())

  return (
    <Modal
      title={title}
      subtitle="It is saved to the masters and selected here."
      wide
      actions={
        <>
          <Button kind="ghost" onClick={onClose}>Cancel</Button>
          <Button kind="primary" disabled={create.isPending || incomplete} onClick={() => create.mutate()}>
            Save and use it
          </Button>
        </>
      }
      onClose={onClose}
    >
      {error ? <Notice kind="bad">{error}</Notice> : null}
      {def.isLoading ? <Loading /> : null}

      <div className="form-grid">
        {asked.map((field) => (
          <Field key={field.key} label={field.label} required={field.required === true} hint={field.hint}>
            {field.control === 'select' ? (
              <Select
                value={values[field.key] ?? ''}
                onChange={(v) => setValues({ ...values, [field.key]: v })}
                placeholder="Not chosen"
                options={[...(field.options ?? [])]}
              />
            ) : (
              <Input
                value={values[field.key] ?? ''}
                onChange={(v) => setValues({ ...values, [field.key]: v })}
                {...(field.control === 'date' ? { type: 'date' } : {})}
              />
            )}
          </Field>
        ))}
      </div>
    </Modal>
  )
}
